import { useCallback, useMemo } from 'react'
import { i18n } from '@fs/zion-locale'
import { useSlsStateContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext } from '../providers/r2Provider/R2Context'

/**
 * Where the user was when they asked to add names.
 *
 * @typedef {'blankState'|'newGroup'|'relationshipsCard'} AddNamesEntryPoint
 */

/** @enum {AddNamesEntryPoint} */
export const ADD_NAMES_ENTRY_POINTS = {
  BLANK_STATE: 'blankState', // The Add Names blank state, and the legacy New household card
  NEW_GROUP: 'newGroup', // The New group card's "Add new person"
  RELATIONSHIPS_CARD: 'relationshipsCard', // A Relationships card's "Add new person"
}

// The props pass for each entry point.
const ENTRY_POINTS = {
  [ADD_NAMES_ENTRY_POINTS.BLANK_STATE]: {
    overlayId: 'addNamesOverlay',
    addingGroupMember: false,
    subtitle: null,
    headingColor: 'yellow',
    title: () => i18n.t('fullText.addNamesPanel.blankState.title', 'Add Names'),
    description: () =>
      i18n.t(
        'fullText.addNamesPanel.description',
        'Enter document details like names, relationships, and events for the person you would like to add.'
      ),
  },
  [ADD_NAMES_ENTRY_POINTS.NEW_GROUP]: {
    overlayId: 'addNamesOverlay',
    addingGroupMember: false,
    subtitle: null,
    headingColor: 'yellow',
    title: () => i18n.t('fullText.addNamesPanel.addNewPerson.title', 'Add New Person'),
    description: () =>
      i18n.t(
        'fullText.addNamesPanel.addNewPerson.description',
        'Enter the details this document records about the person you would like to add.'
      ),
  },
  [ADD_NAMES_ENTRY_POINTS.RELATIONSHIPS_CARD]: {
    overlayId: 'addNewPersonOverlay',
    addingGroupMember: true,
    subtitle: null,
    headingColor: 'yellow',
    title: () => i18n.t('fullText.addNamesPanel.addNewPerson.title', 'Add New Person'),
    description: () =>
      i18n.t(
        'fullText.addNamesPanel.addNewPerson.description',
        'Enter the details this document records about the person you would like to add.'
      ),
  },
}

/**
 * Settings for one entry point.
 *
 * @param {AddNamesEntryPoint} [entryPoint] - Defaults to the blank state.
 * @returns {{overlayId: string, addingGroupMember: boolean, title: string, subtitle: ?string, headingColor: string, description: string}}
 *   Which overlay it opens, whether it is adding to an existing group, and the header copy.
 */
export const getAddNamesEntryPoint = (entryPoint) => {
  const { title, description, ...rest } = ENTRY_POINTS[entryPoint] ?? ENTRY_POINTS[ADD_NAMES_ENTRY_POINTS.BLANK_STATE]

  return { ...rest, title: title(), description: description() }
}

/**
 * One entry point into the Add Names panel: how it looks, and how to open it.
 *
 * `open` puts the intent in R2 state before opening the overlay, because the panel is mounted in a
 * different subtree from most of the buttons that open it. The intent records some details useful for panel processing.
 *
 * @param {AddNamesEntryPoint} [entryPoint] - Defaults to the blank state.
 * @returns {{overlayId: string, addingGroupMember: boolean, title: string, subtitle: ?string, headingColor: string, description: string, open: function}}
 *   The entry point's settings, plus `open({recordId})`.
 */
const useAddNamesEntryPoint = (entryPoint = ADD_NAMES_ENTRY_POINTS.BLANK_STATE) => {
  const { openOverlay } = useOverlayContext()
  const r2Dispatch = useR2DispatchContext()
  const { currentImageArk } = useSlsStateContext()

  const details = useMemo(() => getAddNamesEntryPoint(entryPoint), [entryPoint])

  const open = useCallback(
    ({ recordId = null } = {}) => {
      r2Dispatch({ type: 'SET_ADD_NAMES_INTENT', value: { entryPoint, recordId, imageArk: currentImageArk } })
      openOverlay(details.overlayId)
    },
    [r2Dispatch, openOverlay, entryPoint, details.overlayId, currentImageArk]
  )

  return { ...details, open }
}

export default useAddNamesEntryPoint
